import React, { useState, useEffect, useContext } from 'react';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BookingForm from '../../../../components/customer/PublicCustomerScreens/PackagesComponent/BookingForm';
import {
    FaArrowLeft, FaMapMarkerAlt, FaClock, FaUsers,
    FaTag, FaCheckCircle, FaTimesCircle,
    FaHeart, FaCalendarAlt, FaInfoCircle, FaPhone, FaEnvelope,
} from 'react-icons/fa';
import '../../../../assets/css/customer/publicCustomerCss/CusPackages/cuspackages.css';
import AppContext from '../../../../context/AppContext';


const hasValue = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string')  return v.trim().length > 0;
    if (Array.isArray(v))       return v.length > 0;
    return Boolean(v);
};

const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try { return JSON.parse(field); } catch { return []; }
};

const PackageDetailSubscreen = () => {
    const { id }   = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showBooking, setShowBooking] = useState(false);
    const [pkg, setPkg]                 = useState(null);
    const [recommended, setRecommended] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const { baseUrl, authToken, imageBaseUrl } = useContext(AppContext);

    useEffect(() => {
        setLoading(true);
        setError(null);

        axios
            .get(`${baseUrl}/customer/view-cus-packages`, {
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then((response) => {
                const all = response.data?.data ?? [];
                const found = all.find((p) => String(p.id) === String(id));

                if (!found) {
                    setError('Package not found.');
                    return;
                }

                setPkg(found);

                const others = all
                    .filter((p) => String(p.id) !== String(id))
                    .slice(0, 3);
                setRecommended(others);
            })
            .catch((err) => {
                console.error('Fetch package detail error:', err);
                setError('Unable to load package details. Please try again.');
            })
            .finally(() => setLoading(false));
    }, [id]);
useEffect(() => {
    if (!authToken) return;

    const currentCustomerId = Number(localStorage.getItem('customerId'));
    const currentPackageId  = Number(id);

    axios.get(`${baseUrl}/customer/wishlist`, {
        headers: { Authorization: `Bearer ${authToken}` }
    })
        .then((res) => {
            if (res.data.success) {
                const found = res.data.data.some(
                    (item) =>
                        Number(item.jjcustomer_id) === currentCustomerId &&
                        Number(item.jjpackage_id) === currentPackageId
                );
                setIsWishlisted(found);
            }
        })
        .catch((err) => console.error('Fetch wishlist error:', err));
}, [baseUrl, authToken, id]);
  const handleWishlistToggle = () => {
    if (!authToken) {
        Swal.fire({
            title: 'Login Required',
            text: 'Please login first to save packages to your wishlist.',
            icon: 'info',
            confirmButtonColor: '#2C5F7C',
        });
        return;
    }

    const numericPackageId = Number(id);

    if (isWishlisted) {
        axios.delete(`${baseUrl}/customer/wishlist/${numericPackageId}?type=package`, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then(() => setIsWishlisted(false))
            .catch(() => {});
    } else {
        const formData = new FormData();
        formData.append('jjpackage_id', numericPackageId);

        axios.post(`${baseUrl}/customer/addWishlist`, formData, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then(() => setIsWishlisted(true))
            .catch(() => {});
    }
};
    useEffect(() => {
        const cancelled = searchParams.get('paymentCancelled');
        if (cancelled === 'true') {
            Swal.fire({
                title: 'Payment Cancelled',
                text: 'Your payment was not completed. You can try again anytime.',
                icon: 'info',
                confirmButtonColor: '#2C5F7C',
            });
            window.history.replaceState(null, '', `/packages/${id}`);
        }
    }, [searchParams, id]);

    if (pkg?.id) {
        localStorage.setItem('lastViewedPackage', pkg.id);
    }

    if (loading) return (
        <div className="pub-cus-loading">
            <div className="pub-cus-loading-spinner" />
            <p>Loading package details…</p>
        </div>
    );

    if (error) return (
        <div className="pub-cus-loading">
            <p style={{ color: '#e53e3e' }}>⚠ {error}</p>
            <button onClick={() => navigate('/packages')} style={{ marginTop: '1rem' }}>
                ← Back to Packages
            </button>
        </div>
    );

    if (!pkg) return null;

    const title       = pkg.package_name;
    const category    = pkg.package_category;
    const image = pkg.package_image
        ? `${imageBaseUrl}/${pkg.package_image}`
        : null;
    const price       = pkg.package_price;
    const location    = pkg.location || pkg.package_destination;
    const date       = pkg.package_date;
    const formatDate = (d) => {
        if (!d) return null;
        const dateObj = new Date(d);
        return dateObj.toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const departureDate = formatDate(pkg.package_date);
    const days        = pkg.duration_days;
    const nights      = pkg.duration_nights;
    const maxPeople   = pkg.max_people_size;
    const description = pkg.package_description;

    const inclusions = parseJsonField(pkg.package_inclusion);
    const exclusions = parseJsonField(pkg.package_exclusion);
    const itinerary  = parseJsonField(pkg.package_itinerary);

    const statItems = [
        { icon: <FaMapMarkerAlt />, title: 'Location',   value: location },
        {
            icon: <FaClock />, title: 'Duration',
            value: days && nights
                ? `${days} Days / ${nights} Nights`
                : days ? `${days} Days` : null,
        },
        { icon: <FaUsers />, title: 'Group Size', value: maxPeople ? `Max ${maxPeople} people` : null },
    ].filter(s => hasValue(s.value));

    const showItinerary  = hasValue(itinerary);
    const showInclusions = hasValue(inclusions);
    const showExclusions = hasValue(exclusions);
    const showDescription = hasValue(description);
    const showRecommended = recommended.length > 0;

    const showMiddleCol = showDescription || showInclusions || showExclusions || statItems.length > 0;
    const showRightCol  = showItinerary || showRecommended;

    return (
        <>
            <Modal
                show
                onHide={() => navigate('/packages')}
                fullscreen
                className="pub-cus-package-detail-modal"
            >
                <Modal.Header className="pub-cus-modal-header">
                    <div className="pub-cus-header-content">
                        <button
                            className="pub-cus-back-btn"
                            onClick={() => navigate('/packages')}
                            aria-label="Back to packages"
                        >
                            <FaArrowLeft className="pub-cus-back-icon" />
                            <span>Back</span>
                        </button>
                        <span className="pub-cus-header-package-name">{title}</span>
                    </div>
                </Modal.Header>

                <Modal.Body className="pub-cus-modal-body">

                    <div className="pub-cus-package-header">
                        <div className="pub-cus-title-row">
                            <h1 className="pub-cus-main-title">{title}</h1>
                            {hasValue(category) && (
                                <div className="pub-cus-category-container">
                                    <span className="pub-cus-category-label">Category:</span>
                                    <span className="pub-cus-category-badge">{category}</span>
                                </div>
                            )}
                            <button
                                className={`pub-cus-detail-wishlist-btn${isWishlisted ? ' saved' : ''}`}
                                onClick={handleWishlistToggle}
                                aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                                <FaHeart /> {isWishlisted ? 'Saved' : 'Add to Wishlist'}
                            </button>
                        </div>
                    </div>

                    <div className={`pub-cus-content-wrapper ${!showRightCol ? 'no-right-col' : ''}`}>

                        <div className="pub-cus-left-column">

                            <div className="pub-cus-package-image">
                                {hasValue(image) ? (
                                    <img src={image} alt={title} className="pub-cus-main-img" loading="lazy" />
                                ) : (
                                    <div className="pub-cus-img-placeholder">
                                        <FaMapMarkerAlt />
                                        <span>No image available</span>
                                    </div>
                                )}
                            </div>

                            <div className="pub-cus-book-now-section">
                                <div className="pub-cus-book-card-header">
                                    <h3 className="pub-cus-book-card-title">Ready to Explore?</h3>
                                    <p className="pub-cus-book-card-subtitle">
                                        Book your spot and create memories that last a lifetime
                                    </p>
                                </div>

                                {hasValue(price) && (
                                    <div className="pub-cus-price-display">
                                        <p className="pub-cus-price-label">Starting from</p>
                                        <h2 className="pub-cus-price-amount">PKR {price}</h2>
                                        {departureDate && (
                                            <h2 className="pub-cus-departure_date">
                                                Departure: {departureDate}
                                            </h2>
                                        )}
                                    </div>
                                )}

                                <button
                                    className="pub-cus-book-now-btn"
                                    onClick={() => {
                                        const token = localStorage.getItem("authToken");
                                        const customerId = localStorage.getItem("customerId");

                                        if(!token || !customerId){
                                            localStorage.setItem("pendingBookingPackage", pkg.id);

                                            Swal.fire({
                                                title: 'Login Required',
                                                html: `
                                                    <div style="font-size:15px;color:#555">
                                                        Please login first to continue your booking.
                                                        <br/>
                                                        After login, you will be redirected back here.
                                                    </div>
                                                `,
                                                icon: 'info',
                                                showCancelButton:true,
                                                confirmButtonText:'Login Now',
                                                cancelButtonText:'Cancel',
                                                confirmButtonColor:'#2C5F7C',
                                                cancelButtonColor:'#999',
                                                allowOutsideClick:false,
                                                width:'420px',
                                            }).then((result)=>{
                                                if(result.isConfirmed){
                                                    Swal.fire({
                                                        title:'Redirecting...',
                                                        html:`<div>Taking you to login page</div>`,
                                                        timer:1200,
                                                        timerProgressBar:true,
                                                        showConfirmButton:false,
                                                        allowOutsideClick:false,
                                                        didOpen:()=>{ Swal.showLoading(); }
                                                    }).then(()=>{ navigate('/auth/login'); });
                                                }
                                            });
                                            return;
                                        }

                                        setShowBooking(true);
                                    }}
                                >
                                    <FaCalendarAlt />
                                    <span>Book Now</span>
                                </button>
                            </div>
                        </div>

                        {showMiddleCol && (
                            <div className="pub-cus-middle-column">

                                {statItems.length > 0 && (
                                    <div className="pub-cus-stat-cards">
                                        {statItems.map((s, i) => (
                                            <div key={i} className="pub-cus-stat-card">
                                                <div className="pub-cus-stat-icon">{s.icon}</div>
                                                <div className="pub-cus-stat-content">
                                                    <h4 className="pub-cus-stat-title">{s.title}</h4>
                                                    <p className="pub-cus-stat-value">{s.value}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {showDescription && (
                                    <div className="pub-cus-description-section">
                                        <h3 className="pub-cus-section-title">
                                            <FaTag className="pub-cus-title-icon" /> About This Package
                                        </h3>
                                        <div className="pub-cus-description-content">
                                            <p className="pub-cus-desc-text">{description}</p>
                                        </div>
                                    </div>
                                )}

                                {showInclusions && (
                                    <div className="pub-cus-inclusions-section">
                                        <h3 className="pub-cus-section-title">
                                            <FaCheckCircle className="pub-cus-title-icon" /> What's Included
                                        </h3>
                                        <div className="pub-cus-inclusions-grid">
                                            {inclusions.map((item, i) => (
                                                <div key={i} className="pub-cus-inclusion-item">
                                                    <FaCheckCircle className="pub-cus-inclusion-icon" />
                                                    <span className="pub-cus-inclusion-text">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {showExclusions && (
                                    <div className="pub-cus-exclusions-section">
                                        <h3 className="pub-cus-section-title">
                                            <FaTimesCircle className="pub-cus-title-icon" /> What's Not Included
                                        </h3>
                                        <div className="pub-cus-exclusions-grid">
                                            {exclusions.map((item, i) => (
                                                <div key={i} className="pub-cus-exclusion-item">
                                                    <FaTimesCircle className="pub-cus-exclusion-icon" />
                                                    <span className="pub-cus-exclusion-text">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {showRightCol && (
                            <div className="pub-cus-right-column">

                                {showItinerary && (
                                    <div className="pub-cus-itinerary-section">
                                        <div className="pub-cus-itinerary-header">
                                            <h5 className="pub-cus-itinerary-title">
                                                <FaCalendarAlt className="pub-cus-itinerary-icon" />
                                                Daily Itinerary
                                            </h5>
                                            {days && (
                                                <p className="pub-cus-itinerary-subtitle">
                                                    {days} Day{days > 1 ? 's' : ''} of Adventure
                                                </p>
                                            )}
                                        </div>
                                        <div className="pub-cus-itinerary-list">
                                            {itinerary.map((day, index) => (
                                                <div key={index} className="pub-cus-itinerary-item">
                                                    <div className="pub-cus-itinerary-day-badge">
                                                        Day {day.day || index + 1}
                                                    </div>
                                                    <div className="pub-cus-itinerary-content">
                                                        {hasValue(day.title) && (
                                                            <h6 className="pub-cus-itinerary-day-title">{day.title}</h6>
                                                        )}
                                                        {hasValue(day.description) && (
                                                            <p className="pub-cus-itinerary-day-desc">{day.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {showRecommended && (
                                    <div className="pub-cus-recommended-section">
                                        <div className="pub-cus-recommended-header">
                                            <h5 className="pub-cus-rec-header-title">
                                                <FaHeart className="pub-cus-rec-heart-icon" />
                                                You May Also Like
                                            </h5>
                                            <p className="pub-cus-rec-header-subtitle">
                                                Other amazing destinations
                                            </p>
                                        </div>
                                        <div className="pub-cus-recommended-packages-list">
                                            {recommended.map(rec => (
                                                <div
                                                    key={rec.id}
                                                    className="pub-cus-rec-destination-card"
                                                    onClick={() => navigate(`/packages/${rec.id}`)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={e =>
                                                        e.key === 'Enter' && navigate(`/packages/${rec.id}`)
                                                    }
                                                >
                                                    <div className="pub-cus-rec-card-image-wrapper">
                                                        {hasValue(`${imageBaseUrl}/${rec.package_image}`) ? (
                                                            <img
                                                                src={`${imageBaseUrl}/${rec.package_image}`}
                                                                alt={rec.package_name}
                                                                className="pub-cus-rec-card-image"
                                                                loading="lazy"
                                                            />
                                                        ) : (
                                                            <div className="pub-cus-rec-img-placeholder">
                                                                <FaMapMarkerAlt />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="pub-cus-rec-card-content">
                                                        <h6 className="pub-cus-rec-card-title">{rec.package_name}</h6>
                                                        {hasValue(rec.location || rec.package_destination) && (
                                                            <p className="pub-cus-rec-card-location">
                                                                <FaMapMarkerAlt className="pub-cus-rec-card-icon" />
                                                                {rec.location || rec.package_destination}
                                                            </p>
                                                        )}
                                                        <div className="pub-cus-rec-card-footer">
                                                            {hasValue(rec.package_price) && (
                                                                <span className="pub-cus-rec-card-price">
                                                                    PKR {rec.package_price}
                                                                </span>
                                                            )}
                                                            <button
                                                                className="pub-cus-rec-card-btn"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    navigate(`/packages/${rec.id}`);
                                                                }}
                                                                aria-label={`View ${rec.package_name}`}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="pub-cus-help-section">
                                    <h5 className="pub-cus-help-title">
                                        <FaInfoCircle className="pub-cus-help-icon" /> Need Help?
                                    </h5>
                                    <p className="pub-cus-help-text">
                                        Our travel experts are ready to assist you
                                    </p>
                                    <div className="pub-cus-help-contact">
                                        <div className="pub-cus-help-item">
                                            <FaPhone className="pub-cus-help-contact-icon" />
                                            <span>0328-1334874</span>
                                        </div>
                                        <div className="pub-cus-help-item">
                                            <FaEnvelope className="pub-cus-help-contact-icon" />
                                            <span>journeyjunction@gmail.com</span>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            {showBooking && (
                <Modal
                    show
                    centered
                    size="lg"
                    onHide={() => setShowBooking(false)}
                    className="pub-cus-booking-modal"
                >
                    <Modal.Body className="p-0">
                        <BookingForm
                            packageId={pkg.id}
                            packageData={pkg}
                            onClose={() => setShowBooking(false)}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

export default PackageDetailSubscreen;